import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import clsx from 'clsx';
import { type Day, type POI, type TransportEvent, type DayItem, type AccommodationConfig } from '@poi-planner/shared';
import { formatDate, totalTimeBudget } from '../utils/format';
import { getAccommodationColorClass, getAccommodationPalette, type PaletteEntry } from '../utils/colors';
import { computeDayAutoTimes } from '../utils/autoTime';
import { checkConstraintViolations, type ConstraintViolation } from '../utils/constraints';
import { useUIStore } from '../store/uiStore';
import { WakeUpIndicator } from './WakeUpIndicator';
import { SleepIndicator } from './SleepIndicator';
import { TravelIndicator } from './TravelIndicator';
import { DayItemRenderer, ZonedDayItemRenderer, type ZoneStyle } from './DayItemRenderer';
import { dndIds } from '../utils/dndIds';

interface DayColumnProps {
  day: Day;
  dayIndex: number;
  previousDay: Day | null;
  accommodationPOIs: POI[];
  accommodationConfigs: Record<string, AccommodationConfig>;
  pois: POI[];
  transportEvents: TransportEvent[];
  getAccommodation: (id: string) => POI | undefined;
  getPOI: (id: string) => POI | undefined;
  getNightCount: (dayIndex: number) => { current: number; total: number } | null;
  onDeleteDay: (dayId: string) => void;
  insertIndicator: number | null;
  /** Optional POI order mapping for numbered badges in day-focus mode */
  poiOrderById?: Map<string, number>;
  /** Callback to enter day focus view */
  onEnterFocus?: (dayId: string) => void;
  /** Selected POI ID for highlighting (in focus view) */
  selectedPoiId?: string | null;
  /** Callback when POI is selected (in focus view) */
  onSelectPoi?: (poiId: string | null) => void;
}

/** Inline CSS variable overrides for a palette (used in zone divs) */
function paletteVars(p: PaletteEntry): React.CSSProperties {
  return {
    '--accom-bg': p.bg,
    '--accom-header': p.header,
    '--accom-border': p.border,
    '--accom-text': p.text,
    '--accom-sleep-bg': p.sleepBg,
    '--accom-sleep-border': p.sleepBorder,
    '--accom-sleep-text': p.sleepText,
    '--accom-badge-bg': p.badgeBg,
    '--accom-badge-text': p.badgeText,
  } as React.CSSProperties;
}

export function DayColumn({
  day,
  dayIndex,
  previousDay,
  accommodationPOIs,
  accommodationConfigs,
  pois,
  transportEvents,
  getAccommodation,
  getPOI,
  getNightCount,
  onDeleteDay,
  insertIndicator,
  poiOrderById,
  onEnterFocus,
  selectedPoiId,
  onSelectPoi,
}: DayColumnProps) {
  const openTransportEditor = useUIStore((s) => s.openTransportEditor);

  // Get accommodation POI and config
  const sleepPoiId = day.sleep?.poiId;
  const sleepConfig = sleepPoiId ? accommodationConfigs[sleepPoiId] : undefined;
  const accommodation = sleepPoiId ? getAccommodation(sleepPoiId) : undefined;

  const colorClass = getAccommodationColorClass(sleepConfig);
  const nightCount = getNightCount(dayIndex);

  const sortedPOIs = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);
  const dayTransportEvents = transportEvents.filter((te) => te.dayId === day.id);

  // Determine wake accommodation: for day > 0, use previous day's sleep; for day 0, use current day's sleep as fallback
  const wakePoiId = dayIndex > 0 && previousDay?.sleep
    ? previousDay.sleep.poiId
    : sleepPoiId;
  const wakeConfig = wakePoiId ? accommodationConfigs[wakePoiId] : undefined;
  const wakeAccommodation = wakePoiId ? getAccommodation(wakePoiId) : undefined;

  // Create pseudo-accommodation objects for auto-time calculation (legacy compatibility)
  const wakeAccomForCalc = wakeAccommodation && wakeConfig
    ? { ...wakeAccommodation, checkOutTime: wakeConfig.checkOutTime ?? null, checkInTime: wakeConfig.checkInTime ?? null, color: null }
    : undefined;
  const sleepAccomForCalc = accommodation && sleepConfig
    ? { ...accommodation, checkOutTime: sleepConfig.checkOutTime ?? null, checkInTime: sleepConfig.checkInTime ?? null, color: null }
    : undefined;

  // Auto-time cascade computation
  const autoTimeResult = useMemo(
    () => computeDayAutoTimes(day, pois, dayTransportEvents, wakeAccomForCalc as any, sleepAccomForCalc as any, accommodationPOIs as any),
    [day, pois, dayTransportEvents, wakeAccomForCalc, sleepAccomForCalc, accommodationPOIs],
  );

  // Extract time overruns from auto-time result
  const overrunByPoiId = useMemo(
    () => new Map(autoTimeResult.timeOverruns.map((o) => [o.poiId, o])),
    [autoTimeResult.timeOverruns],
  );

  // Compute constraint violations per POI
  const violationsMap = useMemo(() => {
    const map = new Map<string, ConstraintViolation[]>();
    for (const sp of sortedPOIs) {
      const poi = getPOI(sp.poiId);
      if (!poi) continue;
      const startTime = sp.startTime ?? autoTimeResult.computedStartTimes.get(poi.id) ?? null;
      const violations = checkConstraintViolations(poi, day, startTime);
      if (violations.length > 0) {
        map.set(poi.id, violations);
      }
    }
    return map;
  }, [day, sortedPOIs, autoTimeResult, getPOI]);

  // Build merged items list - filter out POIs that don't exist to match computeDayAutoTimes
  const dayItems: DayItem[] = [
    ...sortedPOIs
      .filter((sp) => getPOI(sp.poiId) !== undefined)
      .map((sp) => ({
        type: 'poi' as const,
        poiId: sp.poiId,
        scheduledPOI: sp,
      })),
    ...dayTransportEvents.map((te) => ({
      type: 'transport' as const,
      event: te,
    })),
  ].sort((a, b) => {
    const orderA = a.type === 'poi' ? a.scheduledPOI.order : a.event.order;
    const orderB = b.type === 'poi' ? b.scheduledPOI.order : b.event.order;
    return orderA - orderB;
  });

  // IDs for sortable context — only POIs are sortable (filter out missing POIs)
  const sortableIds = sortedPOIs
    .filter((sp) => getPOI(sp.poiId) !== undefined)
    .map((sp) => dndIds.dayItem(day.id, sp.poiId));

  // Calculate total time budget for scheduled POIs (only existing ones)
  const totalMinutes = sortedPOIs.reduce((sum, sp) => {
    const poi = getPOI(sp.poiId);
    if (!poi) return sum;
    return sum + poi.timeBudget;
  }, 0);

  // Total warnings for this day
  const totalWarnings = Array.from(violationsMap.values()).reduce((sum, v) => sum + v.length, 0);

  const dateFormatted = formatDate(day.date);

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dndIds.dayContainer(day.id),
  });

  // Next available order for new transport event
  const maxOrder = dayItems.length > 0
    ? Math.max(...dayItems.map((item) => item.type === 'poi' ? item.scheduledPOI.order : item.event.order)) + 1
    : 0;

  const handleAddTransport = () => {
    openTransportEditor('create', undefined, day.id, maxOrder);
  };

  // ── Transport-day gradient detection ──
  const originPalette = getAccommodationPalette(wakeConfig);
  const destPalette = getAccommodationPalette(sleepConfig);

  const isTransportDay = dayTransportEvents.length > 0
    && wakePoiId !== sleepPoiId
    && (wakePoiId != null || sleepPoiId != null);

  // Find the index of the first transport event in dayItems
  const firstTransportIndex = isTransportDay
    ? dayItems.findIndex((item) => item.type === 'transport')
    : -1;

  // Subtitle
  const originName = wakeAccommodation?.name;
  const destName = accommodation?.name;
  const subtitle = isTransportDay && originName && destName
    ? `${dateFormatted} \u2022 ${originName} \u2192 ${destName}`
    : (destName ? `${dateFormatted} \u2022 ${destName}` : dateFormatted);

  // Prepare data for DayItemRenderer
  const travelSegments = autoTimeResult.travelSegments.map((s) => ({ estimate: s.estimate }));
  const totalPOIs = sortedPOIs.filter((sp) => getPOI(sp.poiId) !== undefined).length;

  // Palette vars for zoned rendering
  const originPaletteVars: ZoneStyle = paletteVars(originPalette);
  const destPaletteVars: ZoneStyle = paletteVars(destPalette);

  // Column outer styles (keep as inline for computed gradients)
  const outerBackground = isTransportDay
    ? `linear-gradient(to bottom, ${originPalette.bg}, ${destPalette.bg})`
    : 'var(--accom-bg)';

  const outerBorderColor = isTransportDay
    ? destPalette.border
    : undefined;

  const headerBackground = isTransportDay
    ? `linear-gradient(135deg, ${originPalette.header}, ${destPalette.header})`
    : 'var(--accom-header)';

  const headerBorderColor = isTransportDay
    ? destPalette.border
    : undefined;

  return (
    <div
      className={clsx(colorClass, 'group day-column')}
      style={{
        border: isOver
          ? `2px solid ${isTransportDay ? destPalette.text : 'var(--accom-text)'}`
          : `1px solid ${outerBorderColor ?? 'var(--accom-border)'}`,
        background: outerBackground,
      }}
    >
      {/* Column Header */}
      <div
        className="p-4 flex flex-col gap-1 shrink-0 transition-colors"
        style={{
          borderBottom: `1px solid ${headerBorderColor ?? 'var(--accom-border)'}`,
          background: headerBackground,
        }}
      >
        <div className="flex justify-between items-center">
          <h4
            className="font-bold text-base"
            style={{ color: isTransportDay ? destPalette.text : 'var(--accom-text)' }}
          >
            Day {dayIndex + 1}
          </h4>
          <div className="flex items-center gap-2">
            {totalWarnings > 0 && (
              <span className="warning-badge gap-[3px]">
                <span className="material-symbols-outlined text-[13px]">warning</span>
                {totalWarnings}
              </span>
            )}
            <span className="text-xs font-medium text-text-secondary bg-white/80 px-2 py-1 rounded-md border border-white/20">
              {totalTimeBudget(totalMinutes)}
              {autoTimeResult.totalTravelMinutes > 0 && (
                <span className="text-text-muted font-normal">
                  {' '}+ {autoTimeResult.totalTravelMinutes}m travel
                </span>
              )}
            </span>
            <button
              onClick={() => onDeleteDay(day.id)}
              className="bg-transparent border-none cursor-pointer text-text-muted p-0.5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
              title="Delete day"
            >
              <span className="material-symbols-outlined text-lg">delete</span>
            </button>
            {onEnterFocus && (
              <button
                onClick={() => onEnterFocus(day.id)}
                className="bg-transparent border-none cursor-pointer text-text-muted p-0.5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-blue-500"
                title="Focus on this day"
              >
                <span className="material-symbols-outlined text-lg">fullscreen</span>
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-text-secondary">
          {subtitle}
        </p>
      </div>

      {/* Column Body - Droppable */}
      <div
        ref={setDropRef}
        className="flex-1 overflow-y-auto p-3 flex flex-col gap-0 min-h-[100px] custom-scrollbar"
      >
        <WakeUpIndicator
          dayIndex={dayIndex}
          day={day}
          previousDay={previousDay}
          getAccommodation={getAccommodation}
        />

        {/* First travel segment: wake → first item */}
        <TravelIndicator estimate={autoTimeResult.travelSegments[0]?.estimate ?? null} />

        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {dayItems.length === 0 ? (
            <div
              className={clsx(
                'mt-3 border-2 border-dashed rounded-xl h-40 flex flex-col items-center justify-center text-xs gap-2 transition-all',
                isOver
                  ? 'border-[var(--accom-text)] text-[var(--accom-text)] bg-white/80'
                  : 'border-gray-300 text-text-muted bg-white/30'
              )}
            >
              <span className={clsx('material-symbols-outlined text-3xl', isOver ? 'opacity-100' : 'opacity-50')}>
                {isOver ? 'download' : 'drag_indicator'}
              </span>
              <span className="font-medium">{isOver ? 'Drop here' : 'Drag POIs here'}</span>
            </div>
          ) : isTransportDay && firstTransportIndex >= 0 ? (
            <ZonedDayItemRenderer
              dayId={day.id}
              preTransportItems={dayItems.slice(0, firstTransportIndex)}
              postTransportItems={dayItems.slice(firstTransportIndex)}
              layout="kanban"
              getPOI={getPOI}
              computedStartTimes={autoTimeResult.computedStartTimes}
              travelSegments={travelSegments}
              overrunByPoiId={overrunByPoiId}
              violationsMap={violationsMap}
              insertIndicator={insertIndicator}
              totalPOIs={totalPOIs}
              poiOrderById={poiOrderById}
              originPaletteVars={originPaletteVars}
              destPaletteVars={destPaletteVars}
              firstTransportIndex={firstTransportIndex}
              onSelectPoi={onSelectPoi}
            />
          ) : (
            <DayItemRenderer
              dayId={day.id}
              items={dayItems}
              layout="kanban"
              getPOI={getPOI}
              computedStartTimes={autoTimeResult.computedStartTimes}
              travelSegments={travelSegments}
              overrunByPoiId={overrunByPoiId}
              violationsMap={violationsMap}
              insertIndicator={insertIndicator}
              totalPOIs={totalPOIs}
              poiOrderById={poiOrderById}
              onSelectPoi={onSelectPoi}
            />
          )}
        </SortableContext>

        {/* Add Transport button */}
        <button
          onClick={handleAddTransport}
          className="mt-2 flex items-center justify-center gap-1 p-1.5 border border-dashed border-border rounded-lg bg-transparent text-text-muted text-[11px] font-semibold font-sans cursor-pointer transition-colors hover:border-text-secondary hover:text-text-secondary"
        >
          <span className="material-symbols-outlined text-sm">route</span>
          <span>Add Transport</span>
        </button>
      </div>

      {/* Sleep Footer */}
      <SleepIndicator
        dayIndex={dayIndex}
        day={day}
        previousDay={previousDay}
        accommodationPOIs={accommodationPOIs}
        accommodationConfigs={accommodationConfigs}
        getAccommodation={getAccommodation}
        nightCount={nightCount}
      />
    </div>
  );
}
